<script setup lang="ts">
import ConfirmDialog from 'primevue/confirmdialog'
import Toast from 'primevue/toast'
import { computed, onMounted } from 'vue'

import type { ConfirmKitContent } from '@/shared/composables/useConfirm'
import { useI18n } from '@/app/i18n'
import { actionToasts, dismissActionToast, runActionToast, toastHostReady } from '@/shared/composables/useToast'

import AppCheckbox from './AppCheckbox.vue'
import AppIcon from './AppIcon.vue'
import AppSwitch from './AppSwitch.vue'
import { openDrawers } from './overlay-stack'

onMounted(toastHostReady)
const { t } = useI18n()
/** PrimeVue's severity names, which its toast colour tokens use. */
const TOKEN = { success: 'success', info: 'info', warning: 'warn', danger: 'error' } as const
/**
 * While a drawer is open its header (title, close cross) sits where the toasts start: they start below it instead
 * (queue #58). PrimeVue places the toast with an inline `top`, which this inline style overrides.
 */
const offset = computed(() => (openDrawers.value > 0 ? { top: '4.5rem' } : undefined))

interface Confirmation {
  icon?: string
  message?: string
  kit?: ConfirmKitContent
}

/** The confirmation object `confirm()` & co. emit, as PrimeVue hands it to the message slot. */
const asConfirmation = (message: unknown) => message as Confirmation

function setOption(message: unknown, checked: boolean): void {
  const option = asConfirmation(message).kit?.option
  if (option) option.checked = checked
}
</script>

<template>
  <!-- Mounted once by the app (src/app/App.vue): renders `toast.*` and `confirm()` / `alert()` from anywhere. -->
  <Toast position="top-right" :style="offset" data-kit="toast" />
  <!--
    Toasts with a button ("Undo"): a list of their own, at the toasts' place and in their colours (PrimeVue's toast
    tokens), so the app keeps a single `.p-toast`.
  -->
  <div class="app-action-toasts" :style="offset" aria-live="polite" data-kit="toast-action">
    <TransitionGroup name="app-action-toast">
      <div
        v-for="item in actionToasts"
        :key="item.id"
        class="app-action-toast"
        :style="{
          '--action-toast-bg': `var(--p-toast-${TOKEN[item.variant]}-background)`,
          '--action-toast-border': `var(--p-toast-${TOKEN[item.variant]}-border-color)`,
          '--action-toast-color': `var(--p-toast-${TOKEN[item.variant]}-color)`,
          '--action-toast-shadow': `var(--p-toast-${TOKEN[item.variant]}-shadow)`,
        }"
        role="status"
      >
        <div class="app-action-toast__text">
          <span class="app-action-toast__summary">{{ item.title }}</span>
          <span v-if="item.text" class="app-action-toast__detail">{{ item.text }}</span>
        </div>
        <button
          type="button"
          class="app-action-toast__button"
          data-testid="toast-action"
          @click="runActionToast(item.id)"
        >
          {{ item.action.label }}
        </button>
        <button
          type="button"
          class="app-action-toast__close"
          :aria-label="t('common.action.close')"
          @click="dismissActionToast(item.id)"
        >
          <AppIcon name="x" :size="16" />
        </button>
      </div>
    </TransitionGroup>
  </div>
  <ConfirmDialog :draggable="false" data-kit="confirm">
    <template #message="{ message }">
      <div class="app-confirm">
        <span
          v-if="asConfirmation(message).icon"
          class="app-confirm__icon p-confirmdialog-icon"
          :class="asConfirmation(message).icon"
        />
        <div class="app-confirm__body">
          <p v-if="asConfirmation(message).message" class="app-confirm__text p-confirmdialog-message">
            {{ asConfirmation(message).message }}
          </p>
          <ul v-if="asConfirmation(message).kit?.items.length" class="app-confirm__items" data-testid="confirm-items">
            <li v-for="item in asConfirmation(message).kit?.items" :key="item">{{ item }}</li>
          </ul>
          <template v-if="asConfirmation(message).kit?.option">
            <AppSwitch
              v-if="asConfirmation(message).kit?.option?.kind === 'switch'"
              :model-value="asConfirmation(message).kit?.option?.checked"
              :label="asConfirmation(message).kit?.option?.label"
              class="app-confirm__option"
              data-testid="confirm-option"
              @update:model-value="(checked) => setOption(message, Boolean(checked))"
            />
            <AppCheckbox
              v-else
              :model-value="asConfirmation(message).kit?.option?.checked"
              :label="asConfirmation(message).kit?.option?.label"
              class="app-confirm__option"
              data-testid="confirm-option"
              @update:model-value="(checked) => setOption(message, Boolean(checked))"
            />
          </template>
        </div>
      </div>
    </template>
  </ConfirmDialog>
</template>

<style scoped>
.app-action-toasts {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1200;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: var(--p-toast-width, 25rem);
  max-width: calc(100vw - 40px);
  pointer-events: none;
}
.app-action-toast {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0.857rem 0.857rem 0.857rem 1.143rem;
  border: var(--p-toast-border-width, 1px) solid var(--action-toast-border);
  border-radius: var(--p-toast-border-radius, 12px);
  background: var(--action-toast-bg);
  box-shadow: var(--action-toast-shadow);
  color: var(--action-toast-color);
  backdrop-filter: blur(var(--p-toast-blur, 1.5px));
  pointer-events: auto;
}
.app-action-toast__text {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.app-action-toast__summary {
  font-weight: 600;
}
.app-action-toast__detail {
  font-size: 0.929rem;
}
.app-action-toast__button {
  flex: 0 0 auto;
  height: 32px;
  padding: 0 14px;
  border: 0;
  border-radius: var(--p-border-radius-md);
  background: var(--p-primary-color);
  color: var(--p-primary-contrast-color);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}
.app-action-toast__button:hover {
  background: var(--p-primary-hover-color);
}
.app-action-toast__close {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 14px;
  background: transparent;
  color: inherit;
  cursor: pointer;
}
.app-action-toast__button:focus-visible,
.app-action-toast__close:focus-visible {
  outline: 2px solid var(--p-primary-color);
  outline-offset: 2px;
}
.app-action-toast-enter-active,
.app-action-toast-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.app-action-toast-enter-from,
.app-action-toast-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
@media (prefers-reduced-motion: reduce) {
  .app-action-toast-enter-active,
  .app-action-toast-leave-active {
    transition: none;
  }
}
.app-confirm {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}
.app-confirm__icon {
  flex-shrink: 0;
  font-size: 2rem;
  color: var(--p-orange-500);
}
.app-confirm__icon.pi-exclamation-circle {
  color: var(--p-red-500);
}
.app-confirm__icon.pi-info-circle {
  color: var(--p-blue-500);
}
.app-confirm__icon.pi-check-circle {
  color: var(--p-green-500);
}
.app-confirm__body {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-top: 0.25rem;
}
.app-confirm__text {
  margin: 0;
}
.app-confirm__items {
  margin: 0;
  padding-left: 1.25rem;
  list-style: disc;
}
</style>
